import React, { useEffect, useState } from "react";
import { accountAPI } from "services";
import { Loader, ProfilePhoto } from "components";
import useUniversalStore from "store/universalStore";
import { PhoneInput } from "react-international-phone";
import useOffcanvasStore from "store/offcanvasStore";
import { toast } from "sonner";
import { useForm, Controller } from "react-hook-form";
import { formFields, defaultValues } from "./config";
import "react-international-phone/style.css";
import Creatable from "react-select/creatable";
import { creatableStyle } from "assets/themes";
import { getAllTagsFromAccounts } from "utils";
import { defaultTags } from "utils/tags";

const EditAccount = () => {
  const { accountId, write, accounts } = useUniversalStore((state) => state);
  const tags = getAllTagsFromAccounts(accounts);
  const mappedTags = defaultTags.concat(tags.map((tag) => ({ value: tag, label: tag })));
  const { closeOffcanvas } = useOffcanvasStore();
  const [loading, setLoading] = useState(true);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting, isValid },
  } = useForm({
    defaultValues,
    mode: "onTouched",
  });
  const photo = watch("photo");

  useEffect(() => {
    try {
      console.log("Fetching account data for accountId:", accountId);
      setLoading(true);
      fetchAccountData();
    } catch (error) {
      toast.error("Failed to load account data");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAccountData = async () => {
    const account = await accountAPI.getAccountById(accountId);
    if (account) {
      formFields.forEach((field) => {
        setValue(field.name, account.get(field.name) || "");
      });
      setValue("photo", {
        url: account.get("photo") || null,
        loading: false,
      });
    }
  };

  const onSubmit = async (data) => {
    try {
      await accountAPI.updateAccount(
        accountId,
        data.name,
        data.phone,
        data.address,
        data.details,
        data.tag?.value || data.tag || null,
        data.photo.url
      );

      //Update store updated account and tags
      write({
        accounts: await accountAPI.getAccounts()
      });

      closeOffcanvas();
      toast.success("Account updated successfully");
    } catch (error) {
      toast.error(error.message);
    }
  };

  const renderField = (field) => {
    const commonProps = {
      placeholder: field.placeholder,
      className: `form-control ${
        errors[field.name] ? "is-invalid" : watch(field.name) ? "is-valid" : ""
      }`,
    };

    if (field.name === "tag") {
      return (
        <div className="mb-3" key={field.name}>
          <Controller
            name={field.name}
            control={control}
            render={({ field: { value, onChange } }) => (
              <>
                <Creatable
                  isClearable
                  options={mappedTags}
                  value={
                    mappedTags[
                      mappedTags?.findIndex((tt) => tt.value === value)
                    ]
                  }
                  onChange={onChange}
                  placeholder={field.placeholder}
                  classNamePrefix="rs"
                  styles={creatableStyle}
                  className={`${
                    errors[field.name]
                      ? "is-invalid"
                      : field.value
                      ? "is-valid"
                      : ""
                  }`}
                />
                {errors[field.name] && (
                  <div className="invalid-feedback d-block">
                    {errors[field.name].message}
                  </div>
                )}
              </>
            )}
          />
        </div>
      );
    }

    return (
      <div className="mb-3" key={field.name}>
        <Controller
          name={field.name}
          control={control}
          rules={
            field.rules
              ? {
                  ...field.rules,
                  validate: field.rules.validate
                    ? (value) => field.rules.validate(value, accountId)
                    : undefined,
                }
              : {}
          }
          render={({ field: { ref, ...fieldProps } }) => {
            switch (field.name) {
              case "phone":
                return (
                  <PhoneInput
                    {...fieldProps}
                    {...commonProps}
                    defaultCountry="in"
                    hideDropdown={true}
                    style={{ width: "100%" }}
                  />
                );
              case "textarea":
                return <textarea {...fieldProps} {...commonProps} />;
              default:
                return (
                  <input {...fieldProps} {...commonProps} type={field.type} />
                );
            }
          }}
        />
        {errors[field.name] && (
          <div className="invalid-feedback">{errors[field.name].message}</div>
        )}
      </div>
    );
  };

  if(loading) return <Loader />;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="my-3" align="center">
        <ProfilePhoto
          key="edit-profile-photo"
          url={photo?.url}
          loading={photo?.loading}
          updateField={(field, data) => setValue(field, data)}
        />
      </div>

      {formFields.map(renderField)}

      <br />
      <br />
      <br />

      <div className="row my-3 sticky-bottom-drawer">
        <div className="col-md-12 d-grid">
          <button
            type="submit"
            className="btn btn-app py-3 fs-5 fw-bold text-uppercase"
            disabled={isSubmitting || watch("photo")?.loading || !isValid}
          >
            {watch("photo")?.loading ? (
              <span className="spinner-border spinner-border-sm me-2" />
            ) : isSubmitting ? (
              "Updating..."
            ) : (
              "Update"
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default EditAccount;
